import { useMemo } from "react"
import { Medal, Crown, TrendingUp, Star, Flame } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

const RANK_STYLES = [
  { bg: "bg-amber-50 border-amber-200", text: "text-amber-600", icon: <Crown className="w-5 h-5 text-amber-500" /> },
  { bg: "bg-slate-100 border-slate-200", text: "text-slate-500", icon: <Medal className="w-5 h-5 text-slate-400" /> },
  { bg: "bg-orange-50 border-orange-200", text: "text-orange-600", icon: <Medal className="w-5 h-5 text-orange-400" /> },
]

function computeXP(student) {
  const traitXP = (student.traitEvents ?? []).reduce((sum, e) => {
    const delta = Math.abs(e.delta ?? 0)
    return sum + delta
  }, 0)
  const activityXP = (student.activityLog ?? []).length * 5
  const goalXP = (student.goals ?? []).filter(g => g.status === "completed").length * 20
  const achievementXP = (student.achievements ?? []).filter(a => a.unlocked).length * 15
  return Math.round(traitXP + activityXP + goalXP + achievementXP) || student.totalPoints || 0
}

function computeLevel(xp) {
  return Math.max(1, Math.floor(xp / 100) + 1)
}

export default function LeaderboardPage() {
  const currentUser = AuthStore.getState().currentUser

  const { ranked, currentRank } = useMemo(() => {
    const students = StorageService.read("students") ?? []
    const ranked = students
      .map((s) => ({
        id: s._id ?? s.id ?? s.userId,
        name: `${s.firstName ?? s.name ?? "Student"} ${s.lastName ?? ""}`.trim(),
        avatar: (s.firstName ?? s.name ?? "S")[0].toUpperCase(),
        classLevel: s.classLevel ?? s.preferences?.classLevel ?? "—",
        xp: computeXP(s),
        level: computeLevel(computeXP(s)),
        streak: s.streakDays ?? 0,
        paths: (s.selectedBroadPaths ?? []).length,
        isMe: s.userId === currentUser?._id || s.email === currentUser?.email,
      }))
      .sort((a, b) => b.xp - a.xp)
      .map((s, i) => ({ ...s, rank: i + 1 }))

    const currentRank = ranked.find(s => s.isMe)
    return { ranked, currentRank }
  }, [currentUser])

  const top3 = ranked.slice(0, 3)
  const rest = ranked.slice(3)

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto space-y-8">

            {/* Header */}
            <div>
              <h1 className="text-3xl font-black text-slate-900">Leaderboard</h1>
              <p className="text-slate-500 text-sm mt-1">School-wide ranking by career XP</p>
            </div>

            {/* My rank strip */}
            {currentRank && (
              <div className="bg-indigo-600 text-white rounded-2xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-black text-lg">
                  {currentRank.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">You — {currentRank.name}</p>
                  <p className="text-indigo-200 text-xs">{currentRank.xp} XP · Level {currentRank.level}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-3xl font-black">#{currentRank.rank}</p>
                  <p className="text-indigo-200 text-xs">of {ranked.length}</p>
                </div>
              </div>
            )}

            {/* Podium top 3 */}
            {top3.length > 0 && (
              <div className="flex items-end justify-center gap-4">
                {/* 2nd */}
                {top3[1] && (
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center font-black text-xl ${top3[1].isMe ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "bg-slate-200 border-slate-300 text-slate-600"}`}>
                      {top3[1].avatar}
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-bold text-slate-700 truncate max-w-[80px]">{top3[1].name.split(" ")[0]}</p>
                      <p className="text-xs text-slate-400">{top3[1].xp} XP</p>
                    </div>
                    <div className="w-full bg-slate-300 rounded-t-xl h-16 flex items-center justify-center">
                      <Medal className="w-6 h-6 text-slate-500" />
                    </div>
                  </div>
                )}
                {/* 1st */}
                {top3[0] && (
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <Crown className="w-6 h-6 text-amber-500" />
                    <div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center font-black text-2xl ${top3[0].isMe ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "bg-amber-100 border-amber-300 text-amber-700"}`}>
                      {top3[0].avatar}
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-slate-800 truncate max-w-[90px]">{top3[0].name.split(" ")[0]}</p>
                      <p className="text-xs text-slate-500">{top3[0].xp} XP</p>
                    </div>
                    <div className="w-full bg-amber-400 rounded-t-xl h-24 flex items-center justify-center">
                      <Star className="w-7 h-7 text-white" />
                    </div>
                  </div>
                )}
                {/* 3rd */}
                {top3[2] && (
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center font-black text-xl ${top3[2].isMe ? "bg-indigo-100 border-indigo-400 text-indigo-700" : "bg-orange-100 border-orange-300 text-orange-700"}`}>
                      {top3[2].avatar}
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-bold text-slate-700 truncate max-w-[80px]">{top3[2].name.split(" ")[0]}</p>
                      <p className="text-xs text-slate-400">{top3[2].xp} XP</p>
                    </div>
                    <div className="w-full bg-orange-300 rounded-t-xl h-10 flex items-center justify-center">
                      <Medal className="w-5 h-5 text-white" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Full list */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {ranked.map((s) => {
                const rankStyle = RANK_STYLES[s.rank - 1]
                return (
                  <div
                    key={s.id}
                    className={`flex items-center gap-4 px-5 py-3.5 border-b border-slate-50 last:border-0 transition-colors ${s.isMe ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border shrink-0 ${rankStyle ? `${rankStyle.bg} ${rankStyle.text}` : "bg-slate-50 border-slate-200 text-slate-400"}`}>
                      {rankStyle ? rankStyle.icon : `#${s.rank}`}
                    </div>

                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${s.isMe ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"}`}>
                      {s.avatar}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold truncate ${s.isMe ? "text-indigo-700" : "text-slate-800"}`}>
                        {s.name} {s.isMe && <span className="text-xs font-medium text-indigo-400">(you)</span>}
                      </p>
                      <p className="text-xs text-slate-400">{s.classLevel} · Lv {s.level}</p>
                    </div>

                    {/* Streak */}
                    {s.streak > 0 && (
                      <div className="flex items-center gap-1 text-xs font-semibold text-orange-500">
                        <Flame className="w-3.5 h-3.5" /> {s.streak}d
                      </div>
                    )}

                    {/* XP */}
                    <div className="text-right shrink-0">
                      <p className="text-sm font-black text-slate-800">{s.xp.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide">XP</p>
                    </div>
                  </div>
                )
              })}

              {ranked.length === 0 && (
                <div className="text-center py-16 text-slate-400">
                  <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No students on the board yet.</p>
                  <p className="text-sm mt-1">Complete activities to earn XP and appear here.</p>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
